import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TRIABLES, PER_DEFECTE } from '../tema';
import { nomsCapAIds, idsCapANoms, analitza } from '../expressions';

// `flotant` fa que el panell surti damunt del llenç en comptes d'ocupar-li 320 px d'amplada,
// i que **no existeixi si no hi ha res seleccionat**. Així el dibuix no es mou de lloc cada cop
// que es tria o es deixa de triar un node, que és el que passava i desorientava mentre col·locaves.
const EnhancedPropertiesPanel = ({ component, onUpdateComponent, flotant = false, onTancar,
                                  components = [] }) => {
  const { t, i18n } = useTranslation();
  const [localComponent, setLocalComponent] = useState(null);
  // El text de l'equació TAL COM S'ESCRIU, amb noms. El que es desa porta identificadors.
  const [equacio, setEquacio] = useState('');

  useEffect(() => {
    if (component) {
      setLocalComponent({ ...component });
      setEquacio(idsCapANoms(component.properties?.equation || '', components, i18n.language));
    } else {
      setLocalComponent(null);
      setEquacio('');
    }
    // `components` a fora a posta: si hi entrés, rebatejar un component mentre s'escriu
    // reescriuria el camp a mitja frase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component]);

  const errorEquacio = (() => {
    if (!localComponent) return null;
    const cru = localComponent.properties?.equation || '';
    if (!cru.trim()) return null;
    const r = analitza(cru);
    return r.error ? { clau: r.error, detall: r.detall } : null;
  })();

  const handleChange = (field, value) => {
    if (!localComponent) return;
    
    const updated = { ...localComponent, [field]: value };
    setLocalComponent(updated);
    
    // Auto-save on change (with debounce)
    clearTimeout(window.propertyUpdateTimeout);
    window.propertyUpdateTimeout = setTimeout(() => {
      onUpdateComponent(updated);
    }, 300);
  };

  const handleNestedChange = (parent, field, value) => {
    if (!localComponent) return;
    
    const updated = {
      ...localComponent,
      [parent]: {
        ...localComponent[parent],
        [field]: value
      }
    };
    setLocalComponent(updated);
    
    // Auto-save on change (with debounce)
    clearTimeout(window.propertyUpdateTimeout);
    window.propertyUpdateTimeout = setTimeout(() => {
      onUpdateComponent(updated);
    }, 300);
  };

  if (!localComponent) {
    if (flotant) return null;
    return (
      <div className="w-80 bg-white border-l border-gray-200 p-4">
        <div className="text-center text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm">{t('properties.none')}</p>
        </div>
      </div>
    );
  }

  // Determine component type
  const isVariable = localComponent.type === 'variable';
  // Pel camp, no pel prefix de l'identificador: un lligam que ve de la base té un UUID i el
  // prefix `rel_` ja no hi és. Es manté el prefix com a alternativa per als que s'acaben de
  // dibuixar i encara no s'han desat.
  const isRelationship = localComponent.type === 'relationship'
    || localComponent.id?.startsWith('rel_');
  const isStock = localComponent.type === 'stock';
  const isFlow = localComponent.type === 'flow';
  const isConverter = localComponent.type === 'converter';
  const isCloud = localComponent.type === 'cloud';
  const isConnector = localComponent.type === 'connector';
  // Les dues menes de fletxa del llenç d'estocs. Cap de les dues porta valor inicial ni posició
  // que valgui la pena ensenyar: el que les defineix són els seus extrems.
  const esFletxaSF = isFlow || isConnector;
  // Un núvol no porta nom ni res: és el que queda FORA de la frontera del model, i posar-hi un
  // camp de nom seria convidar a descriure allò de què, per definició, no en sabem res.
  const nomDe = (id) => components.find((c) => String(c.id) === String(id))?.name
    || t(`tools.${components.find((c) => String(c.id) === String(id))?.type || 'cloud'}`);

  // El títol es muntava concatenant text anglès amb el tipus en brut («Variable Properties»), i
  // per això no el va veure l'escombrada de la traducció.
  const titol = isRelationship ? t('properties.titleRelationship')
    : isVariable ? t('properties.titleVariable')
    : (isStock || isFlow || isConverter || isCloud || isConnector) ? t(`tools.${localComponent.type}`)
    : t('properties.titleGeneric');

  return (
    <div className={flotant
      ? 'absolute right-3 top-3 z-20 w-72 max-h-[calc(100%-1.5rem)] bg-white border border-gray-200 rounded-md shadow-lg flex flex-col'
      : 'w-80 bg-white border-l border-gray-200 flex flex-col h-full'}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{titol}</h3>
          <p className="text-xs text-gray-500 mt-1">{t('props.autosave')}</p>
        </div>
        {flotant && (
          <button type="button" onClick={onTancar} aria-label={t('help.close')}
            className="text-gray-500 hover:text-gray-700 text-xl leading-none -mt-1">&times;</button>
        )}
      </div>
      
      {/* Properties */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {/* Common Properties */}
          {isCloud && (
            <p className="text-sm text-gray-600">{t('sfAjuda.cloudBody')}</p>
          )}

          {!isRelationship && !isCloud && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.name')}</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                value={localComponent.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('props.namePlaceholder')}
              />
            </div>
          )}

          {/* Variable-specific Properties */}
          {isVariable && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.notes')}</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={localComponent.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('props.descPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.initial')}</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    value={localComponent.properties?.initialValue || 50}
                    onChange={(e) => handleNestedChange('properties', 'initialValue', parseInt(e.target.value) || 50)}
                  />
                  <span className="text-sm text-gray-500">0-100</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('props.colour')}</label>
                <div className="grid grid-cols-5 gap-2">
                  {/* Predefined colors */}
                  {[
                    ...TRIABLES.map((c) => ({ name: c.nom, value: c.valor }))
                  ].map((color) => (
                    <button
                      key={color.value}
                      className={`w-10 h-10 rounded-md border-2 transition-all ${
                        localComponent.properties?.color === color.value 
                          ? 'border-gray-900 scale-110' 
                          : 'border-gray-300 hover:border-gray-500'
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => handleNestedChange('properties', 'color', color.value)}
                      title={color.name}
                    />
                  ))}
                  {/* Custom color picker */}
                  <div className="relative">
                    <input
                      type="color"
                      className="w-10 h-10 rounded-md border-2 border-gray-300 cursor-pointer"
                      value={localComponent.properties?.color || PER_DEFECTE}
                      onChange={(e) => handleNestedChange('properties', 'color', e.target.value)}
                      title="Custom color"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Relationship-specific Properties */}
          {isRelationship && (
            <>
              {/* El nom de la relació. El camp de nom de dalt s'amaga per a les relacions, i
                  fins ara no en tenien cap: al llenç totes les fletxes eren mudes. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('props.relName')}</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={localComponent.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={t('props.relNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('props.polarity')}</label>
                <div className="flex space-x-2">
                  <button
                    className={`flex-1 px-3 py-2 rounded-md border-2 transition-colors ${
                      localComponent.polarity === '+' 
                        ? 'bg-ok-50 border-ok-500 text-ok-700' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => handleChange('polarity', '+')}
                  >
                    Positive (+)
                  </button>
                  <button
                    className={`flex-1 px-3 py-2 rounded-md border-2 transition-colors ${
                      localComponent.polarity === '-' 
                        ? 'bg-danger-50 border-danger-500 text-danger-700' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => handleChange('polarity', '-')}
                  >
                    Negative (-)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('props.delay')}</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="10"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    value={localComponent.delay || 0}
                    onChange={(e) => handleChange('delay', parseInt(e.target.value) || 0)}
                  />
                  <span className="text-sm text-gray-500">{t('props.timeUnits')}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('props.strength')}</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={localComponent.strength || 'medium'}
                  onChange={(e) => handleChange('strength', e.target.value)}
                >
                  <option value="weak">{t('props.weak')}</option>
                  <option value="medium">{t('props.medium')}</option>
                  <option value="strong">{t('props.strong')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.notes')}</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={localComponent.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('props.relPlaceholder')}
                />
              </div>
            </>
          )}

          {/* Stock-specific Properties */}
          {isStock && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.initial')}</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={localComponent.properties?.initialValue || 0}
                  onChange={(e) => handleNestedChange('properties', 'initialValue', parseFloat(e.target.value) || 0)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.units')}</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={localComponent.properties?.units || ''}
                  onChange={(e) => handleNestedChange('properties', 'units', e.target.value)}
                  placeholder={t('sf.unitsPlaceholderStock')}
                />
              </div>
            </>
          )}

          {/* Els extrems d'una fletxa del llenç d'estocs. No s'editen aquí: una fletxa que
              apunta a un altre lloc és una altra fletxa, i canviar-la escrivint-hi un
              identificador seria demanar que algú els sabés de memòria. */}
          {esFletxaSF && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="block text-xs font-medium text-gray-700 mb-1">{t('sf.from')}</span>
                <span className="block px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 truncate">
                  {localComponent.properties?.from ? nomDe(localComponent.properties.from) : t('sf.noEndpoint')}
                </span>
              </div>
              <div>
                <span className="block text-xs font-medium text-gray-700 mb-1">{t('sf.to')}</span>
                <span className="block px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 truncate">
                  {localComponent.properties?.to ? nomDe(localComponent.properties.to) : t('sf.noEndpoint')}
                </span>
              </div>
            </div>
          )}

          {/* Flow/Converter-specific Properties */}
          {(isFlow || isConverter) && (
            <>
              {/* L'EQUACIÓ S'ESCRIU AMB NOMS I ES DESA AMB IDENTIFICADORS.
                  Escriure `Dipòsit * Taxa` és el que la gent espera i el que fan Vensim i Stella,
                  però desar-ho així voldria dir que rebatejar un component —que el llenç deixa fer
                  amb doble clic— trencaria totes les equacions que l'anomenessin, i en silenci.
                  La conversió va als dos sentits i és a `expressions.js`, amb proves. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.equation')}</label>
                <textarea
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500
                              focus:border-primary-500 font-mono text-sm transition-colors ${
                    errorEquacio ? 'border-warn-500' : 'border-gray-300'}`}
                  value={equacio}
                  onChange={(e) => {
                    setEquacio(e.target.value);
                    handleNestedChange('properties', 'equation', nomsCapAIds(e.target.value, components));
                  }}
                  placeholder={t('sf.eqPlaceholder')}
                />
                {errorEquacio
                  ? <p className="mt-1 text-xs text-warn-700">{t(errorEquacio.clau, { detall: errorEquacio.detall || '' })}</p>
                  : <p className="mt-1 text-xs text-gray-500">{t('sf.eqAjuda')}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.units')}</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  value={localComponent.properties?.units || ''}
                  onChange={(e) => handleNestedChange('properties', 'units', e.target.value)}
                  placeholder={t('sf.unitsPlaceholderFlow')}
                />
              </div>
            </>
          )}

          {/* Common Documentation */}
          {!isRelationship && !isCloud && !isConnector && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('properties.notes')}</label>
              <textarea
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                value={localComponent.documentation || ''}
                onChange={(e) => handleChange('documentation', e.target.value)}
                placeholder={t('props.notesPlaceholder')}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Metadata Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex justify-between">
            <span className="font-medium">{t('props.type')}:</span>
            <span>{isRelationship ? t('properties.titleRelationship') : t(`tools.${localComponent.type}`)}</span>
          </div>
          {!isRelationship && !esFletxaSF && (
            <div className="flex justify-between">
              <span className="font-medium">{t('props.position')}:</span>
              <span>X: {Math.round(localComponent.x || 0)}, Y: {Math.round(localComponent.y || 0)}</span>
            </div>
          )}
          {localComponent.id && (
            <div className="flex justify-between">
              <span className="font-medium">{t('props.id')}:</span>
              <span className="font-mono">{localComponent.id}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedPropertiesPanel;